import {
	IconArrowRight,
	IconFileText,
	IconLock,
	IconScale,
} from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { allLegalDocuments } from "content-collections";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { MarketingAnimated } from "@/components/misc/marketing-animated";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { listLegalDocumentsForIndex } from "@/lib/legal-versions";
import {
	isLegalDocumentModuleEnabled,
	isOperationsLegalEnabled,
	isOperationsSecurityEnabled,
} from "@/lib/modules";
import {
	MARKETING_GRID_CARD_OVERLAY_CLASSES,
	MARKETING_GRID_CARD_SURFACE,
} from "@/utils/animation";
import { seo } from "@/utils/seo";

const enabledDocuments = () =>
	listLegalDocumentsForIndex(allLegalDocuments).filter((document) =>
		isLegalDocumentModuleEnabled(document.module),
	);

export const Route = createFileRoute("/_marketing/legal/")({
	component: RouteComponent,
	beforeLoad: () => {
		if (!isOperationsLegalEnabled() && !isOperationsSecurityEnabled()) {
			throw redirect({ to: "/" });
		}
	},
	head: () => ({
		...seo({
			title: "Legal and Security",
			description:
				"Find legal, privacy, security, subprocessor, and responsible disclosure documents.",
			keywords: "legal, privacy, security, terms, subprocessors",
		}),
	}),
});

function getDocumentIcon(module: "legal" | "security") {
	return module === "security" ? IconLock : IconScale;
}

export function RouteComponent() {
	const documents = enabledDocuments();

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer className="pb-8">
					<MarketingHeader
						title="Operations"
						titleIcon={IconFileText}
						heading="Legal and security documents"
						description="Common policies, notices, and security information for customers, app stores, API consumers, and procurement reviews."
					/>

					<MarketingAnimated delayMs={200} className="mb-12">
						<div className="mb-8 flex items-center gap-3">
							<IconScale className="h-5 w-5 text-primary" aria-hidden />
							<h2 className="text-2xl font-semibold tracking-tight text-foreground">
								Documents
							</h2>
							<Badge variant="outline" className="ml-2">
								{documents.length}{" "}
								{documents.length === 1 ? "document" : "documents"}
							</Badge>
						</div>

						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
							{documents.map((document, index) => {
								const Icon = getDocumentIcon(document.module);
								return (
									<MarketingAnimated
										key={document.documentId}
										delayMs={400 + index * 100}
										className="h-full"
									>
										<Link
											to="/legal/$slug"
											params={{ slug: document.documentId }}
											className="group block h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<Card className={MARKETING_GRID_CARD_SURFACE}>
												<div className={MARKETING_GRID_CARD_OVERLAY_CLASSES} />

												<CardHeader className="relative flex-1">
													<div className="mb-3 flex items-start justify-between">
														<Badge variant="secondary" className="text-xs">
															{document.module === "security"
																? "Security"
																: "Legal"}
														</Badge>
														<Icon
															className="size-4 text-muted-foreground"
															aria-hidden
														/>
													</div>

													<CardTitle className="mb-3 line-clamp-2 text-xl font-semibold text-foreground transition-colors group-hover:text-primary">
														{document.title}
													</CardTitle>

													<CardDescription className="line-clamp-3 text-sm leading-relaxed">
														{document.summary}
													</CardDescription>
												</CardHeader>

												<CardContent className="relative mt-auto">
													<div className="flex items-center justify-between">
														<p className="text-sm text-muted-foreground">
															Updated{" "}
															{new Date(
																document.lastUpdated,
															).toLocaleDateString("en-US", {
																year: "numeric",
																month: "short",
																day: "numeric",
															})}
														</p>
														<div className="flex items-center gap-2 text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
															<span className="text-sm font-medium">Read</span>
															<IconArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
														</div>
													</div>
												</CardContent>
											</Card>
										</Link>
									</MarketingAnimated>
								);
							})}
						</div>
					</MarketingAnimated>

					<MarketingAnimated
						delayMs={500}
						className="rounded-2xl border border-border/80 bg-muted/15 p-8 text-center dark:bg-muted/10 sm:p-10"
					>
						<h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
							Questions about our policies?
						</h2>
						<p className="mx-auto mt-2 max-w-md text-pretty text-muted-foreground">
							Contact us from the app or reach out through our support channels
							if you need help interpreting these documents.
						</p>
						<Button
							className="mt-6 rounded-full px-6"
							size="lg"
							onClick={() => window.location.assign("/contact")}
						>
							Contact support
							<IconArrowRight className="size-4" aria-hidden />
						</Button>
					</MarketingAnimated>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
